import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { config } from "@/shared/lib/config";
import { fetcher } from "@/shared/lib/fetcher";

export type JamEventSettingsDTO = {
  defaultStudentGroupId?: string | null;
};

export type JamEventDTO = {
  id: string;
  slug: string;
  title: string;
  status: "draft" | "open" | "closed";
  opensAt: string | null;
  closesAt: string | null;
  settings?: JamEventSettingsDTO;
};

export type JamEventCategoryDTO = {
  id: string;
  eventId: string;
  categoryKey: string;
  name: string;
  publicLabelLao: string | null;
  description: string | null;
  format: "solo" | "duo";
  kind: "qualifier" | "battle";
  sortOrder: number;
  enabled: boolean;
  capacity: number | null;
  claimed?: number;
  remaining?: number | null;
  availability?: string;
};

export type JamRegistrationListItemDTO = {
  id: string;
  fullName: string;
  nickname: string | null;
  phoneNumber: string;
  teamName: string | null;
  status: string;
  submittedAt: string;
  assignedTo: string | null;
  category: {
    id: string;
    categoryKey: string;
    name: string;
    format: string;
    kind: string;
  } | null;
};

export type JamSimilarStudentMatchDTO = {
  id: string;
  memberId: string;
  name: string;
  phoneNumber: string | null;
};

export type JamRegistrationDetailDTO = {
  id: string;
  eventId: string;
  categoryId: string;
  fullName: string;
  nickname: string | null;
  phoneNumber: string;
  teamName: string | null;
  partnerFullName: string | null;
  partnerNickname: string | null;
  partnerPhone: string | null;
  status: string;
  adminNotes: string | null;
  submittedAt: string;
  studentId: string | null;
  videoKey: string | null;
  videoDurationSeconds: number | null;
  photoKey: string | null;
  previewStudentName: string;
  videoReadUrl?: string | null;
  photoReadUrl?: string | null;
  videoDownloadUrl?: string | null;
  photoDownloadUrl?: string | null;
  category: JamEventCategoryDTO | null;
  event: { id: string; title: string; slug: string; settings?: JamEventSettingsDTO | null } | null;
};

export const jamRegistrationsApi = {
  listEvents: () =>
    fetcher.get<{ items: JamEventDTO[] }>(
      `${config.apiUrl}/jam-registrations/events`,
    ),

  initializeFanglaoJam2026: () =>
    fetcher.post<{
      eventId: string;
      slug: string;
      eventCreated: boolean;
      categoryCount: number;
    }>(`${config.apiUrl}/jam-registrations/events/initialize-fanglao-jam-2026`),

  getEvent: (id: string) =>
    fetcher.get<JamEventDTO & { categories: JamEventCategoryDTO[] }>(
      `${config.apiUrl}/jam-registrations/events/${id}`,
    ),

  updateEvent: (
    id: string,
    body: Partial<JamEventDTO> & { settings?: JamEventSettingsDTO },
  ) =>
    fetcher.put<JamEventDTO>(
      `${config.apiUrl}/jam-registrations/events/${id}`,
      body,
    ),

  updateCategory: (
    eventId: string,
    categoryId: string,
    body: {
      enabled?: boolean;
      capacity?: number | null;
      publicLabelLao?: string | null;
      description?: string | null;
    },
  ) =>
    fetcher.patch<JamEventCategoryDTO>(
      `${config.apiUrl}/jam-registrations/events/${eventId}/categories/${categoryId}`,
      body,
    ),

  listRegistrations: (
    query: OffsetPageQueryDTO & {
      eventId?: string;
      status?: string;
      categoryId?: string;
      search?: string;
    },
  ) => {
    const url = new URL(`${config.apiUrl}/jam-registrations/registrations`);
    url.searchParams.set("limit", String(query.limit ?? 20));
    url.searchParams.set("offset", String(query.offset ?? 0));
    if (query.sort) url.searchParams.set("sort", JSON.stringify(query.sort));
    if (query.eventId) url.searchParams.set("eventId", query.eventId);
    if (query.status) url.searchParams.set("status", query.status);
    if (query.categoryId) url.searchParams.set("categoryId", query.categoryId);
    if (query.search) url.searchParams.set("search", query.search);
    return fetcher.get<{
      data: JamRegistrationListItemDTO[];
      meta: { total: number; offset: number; limit: number };
    }>(url.toString());
  },

  getRegistration: (id: string) =>
    fetcher.get<JamRegistrationDetailDTO>(
      `${config.apiUrl}/jam-registrations/registrations/${id}`,
    ),

  updateRegistration: (
    id: string,
    body: {
      status?: string;
      adminNotes?: string | null;
      assignedTo?: string | null;
      categoryId?: string;
    },
  ) =>
    fetcher.patch(
      `${config.apiUrl}/jam-registrations/registrations/${id}`,
      body,
    ),

  convertRegistration: async (
    id: string,
    body: {
      linkExistingStudentId?: string;
      groupId?: string | null;
      additionalNotes?: string | null;
      forceCreateNew?: boolean;
    },
  ) => {
    const res = await fetch(
      `${config.apiUrl}/jam-registrations/registrations/${id}/convert`,
      {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      },
    );

    const data = (await res.json().catch(() => ({}))) as {
      ok?: boolean;
      studentId?: string;
      memberId?: string;
      linked?: boolean;
      code?: string;
      matches?: JamSimilarStudentMatchDTO[];
    };

    if (!res.ok) {
      if (res.status === 409 && data.code === "SIMILAR_STUDENTS" && data.matches) {
        const err = new Error(data.code) as Error & {
          code: string;
          matches: JamSimilarStudentMatchDTO[];
        };
        err.code = data.code;
        err.matches = data.matches;
        throw err;
      }
      throw new Error(data.code ?? "CONVERT_FAILED");
    }

    return data as {
      ok: true;
      studentId: string;
      memberId: string;
      linked: boolean;
    };
  },

  exportCsvUrl: (eventId: string) =>
    `${config.apiUrl}/jam-registrations/registrations/export.csv?eventId=${encodeURIComponent(eventId)}`,
};
